import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { MapOutlined } from '@mui/icons-material';
import { loadGoogleMaps } from '../../utils/googleMapsLoader';
import { status as statusColors, brand, text } from '../../theme/colors';

// The public pin is rounded to the 0.01° grid (≈1 km). A 700 m circle reads as
// "somewhere in this suburb" without suggesting the stand is at its centre.
const SUBURB_RADIUS_M = 700;
const SUBURB_ZOOM = 14;
const EXACT_ZOOM = 16;

/**
 * The "Location" map on a listing. Until the buyer enquires, the payload only
 * carries a suburb-precision point, and this map must not pretend otherwise:
 * it draws a circle, not a marker, and says so underneath. Once they have
 * enquired the server sends the real pin and the map drops a marker on it.
 */
export default function SuburbMap({ pin, height = 260 }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const shapeRef = useRef(null);
  const aliveRef = useRef(true);
  const [unavailable, setUnavailable] = useState(false);

  const lat = Number(pin?.lat);
  const lng = Number(pin?.lng);
  const hasPin = Number.isFinite(lat) && Number.isFinite(lng);
  const exact = pin?.precision === 'exact';

  useEffect(() => {
    aliveRef.current = true;
    if (!hasPin) return undefined;

    loadGoogleMaps()
      .then((maps) => {
        if (!aliveRef.current || !elRef.current) return;
        const center = { lat, lng };
        if (!mapRef.current) {
          mapRef.current = new maps.Map(elRef.current, {
            center,
            zoom: exact ? EXACT_ZOOM : SUBURB_ZOOM,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            clickableIcons: false,
            // A small map inside a scrolling page should not swallow the scroll.
            gestureHandling: 'cooperative',
          });
        } else {
          mapRef.current.setCenter(center);
          mapRef.current.setZoom(exact ? EXACT_ZOOM : SUBURB_ZOOM);
        }
        if (shapeRef.current) { shapeRef.current.setMap(null); shapeRef.current = null; }
        shapeRef.current = exact
          ? new maps.Marker({ position: center, map: mapRef.current })
          : new maps.Circle({
            center,
            radius: SUBURB_RADIUS_M,
            map: mapRef.current,
            strokeColor: brand.primary,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: brand.primary,
            fillOpacity: 0.15,
            clickable: false,
          });
      })
      .catch(() => { if (aliveRef.current) setUnavailable(true); });

    // Only the drawn shape is torn down. The map itself is deliberately kept:
    // this effect re-runs when the pin sharpens from suburb to exact after an
    // enquiry, and discarding the map there would build a second one on top of
    // the first inside the same div.
    return () => {
      aliveRef.current = false;
      if (shapeRef.current) { shapeRef.current.setMap(null); shapeRef.current = null; }
    };
  }, [hasPin, lat, lng, exact]);

  if (!hasPin) return null;

  const caption = exact
    ? 'Exact location.'
    : 'Exact stand shown after you enquire.';

  if (unavailable) {
    return (
      <Box>
        <Box
          sx={{
            height, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 1, bgcolor: statusColors.neutralBg, borderRadius: 2,
          }}
        >
          <MapOutlined sx={{ fontSize: 36, color: text.tertiary }} />
          <Typography variant="body2" sx={{ color: text.secondary, fontWeight: 600 }}>Map unavailable</Typography>
        </Box>
        <Typography variant="caption" sx={{ color: text.tertiary, display: 'block', mt: 1 }}>{caption}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        ref={elRef}
        role="region"
        aria-label={exact ? 'Map of the property location' : 'Map of the suburb this property is in'}
        sx={{ height, borderRadius: 2, overflow: 'hidden' }}
      />
      <Typography variant="caption" sx={{ color: text.tertiary, display: 'block', mt: 1 }}>{caption}</Typography>
    </Box>
  );
}
